import * as d3 from 'd3';
import { Selection, BaseType } from 'd3-selection';
import { Simulation } from 'd3-force';
import { Gui } from '../../gui';
import { INodeData, ILinkData, INodeDataElement } from './context-detail-contracts';

const tickDrawPoint = 30;
const svgSelector = '.context-detail-component__svg';
export const detailsInfoSelector = 'context-details-info';

export abstract class DetailComponent<T extends INodeDataElement, N extends INodeData, L extends ILinkData<N>> {
  private svgCanvas: Selection<SVGSVGElement, any, HTMLElement, any>;
  protected relationsCanvas: d3.Selection<SVGGElement, any, HTMLElement, any>;
  protected svgCanvasWidth: number;
  protected svgCanvasHeight: number;
  protected centerX: number;
  protected centerY: number;
  protected centerCircleR: number;
  protected centerCircleDetailsWidth: number;
  protected relationCircleR: number;
  private linkLength: number;

  protected selectedElement: T;

  private simulation: Simulation<N, L>;

  protected nodes: N[];
  protected links: L[];

  private linkSvgElements: d3.Selection<SVGLineElement, L, SVGGElement, any>;
  private linkLabelSvgElements: d3.Selection<SVGTextElement, L, SVGGElement, any>;
  private nodeSvgElements: d3.Selection<SVGCircleElement, N, SVGGElement, any>;
  private nodeLabelSvgElements: d3.Selection<SVGTextElement, N, SVGGElement, any>;

  protected centerTopDetails: any;
  protected centerBottomDetails: any;

  private tick: number;

  constructor(protected gui: Gui, protected jQueryElement: JQuery<HTMLElement>) {
    this.svgCanvas = d3.select(svgSelector);
    this.relationsCanvas = this.svgCanvas.append('g').attr('class', 'context-detail-component__svg__relations');
    this.init();
  }

  public abstract setElement(elementId: string): void;
  protected abstract initLinks(): void;
  protected abstract cleanLinks(): void;

  public init() {
    this.calculateCanvasPoints();
    this.setSelectedInfo();
  }

  public cleanCanvas() {
    if (this.simulation) {
      this.simulation.stop();
    }
    this.relationsCanvas.selectAll('*').remove();
    this.cleanLinks();
    this.tick = 0;
  }

  protected openUrlInNewTab(url: string) {
    const tab = window.open(url, '_blank');
    tab.focus();
  }

  protected calculateCanvasPoints() {
    this.svgCanvasWidth = this.svgCanvas.node().getBoundingClientRect().width;
    this.svgCanvasHeight = this.svgCanvas.node().getBoundingClientRect().height;
    this.centerX = this.svgCanvasWidth / 2;
    this.centerY = this.svgCanvasHeight / 2;
    const minSize = Math.min(this.svgCanvasWidth, this.svgCanvasHeight);
    this.centerCircleR = minSize / 15;
    this.relationCircleR = minSize / 40;
    this.centerCircleDetailsWidth = this.svgCanvasWidth / 4;
    this.linkLength = this.centerCircleR * (this.svgCanvasWidth / (this.centerCircleR * 4.2));
  }

  protected setSelectedInfo() {
    if (!this.selectedElement) {
      return;
    }
    this.cleanCanvas();
    this.calculateCanvasPoints();
    this.initLinks();
    this.buildSimulationData();
    this.initSimulation();
  }

  /**
   * Simulation stuff
   */

  protected abstract buildSimulationData(): void;
  protected abstract forceXNode(node: N): number;
  protected abstract forceYNode(node: N): number;

  protected abstract linkClass(link: L): string;
  protected abstract linkLabelClass(link: L): string;
  protected abstract linkLabelText(link: L): string;
  protected abstract linkLabelX(link: L): number;
  protected abstract linkLabelY(link: L): number;

  protected abstract nodeClass(node: N): string;
  protected abstract nodeCX(node: N): number;
  protected abstract nodeLabelX(node: N, index: number, labels: d3.BaseType[] | d3.ArrayLike<BaseType>): number;
  protected abstract handleNodeClick(node: N): void;

  protected abstract getCenterNodeDetails(): { topText: string; bottomText: string };

  private initSimulation() {
    if (this.simulation) {
      // stop previous simulation if it exists
      this.simulation.stop();
    }
    this.simulation = d3
      .forceSimulation<N, L>(this.nodes)
      .force(
        'link',
        d3
          .forceLink<N, L>()
          .id((node) => node.element.Id)
          .distance(this.linkLength)
          .strength(2)
      )
      .force('x', d3.forceX<N>((node) => this.forceXNode(node)).strength((node) => (node.element.Id === this.selectedElement.Id ? 3 : 1)))
      .force('y', d3.forceY<N>((node) => this.forceYNode(node)).strength((node) => (node.element.Id === this.selectedElement.Id ? 3 : 0.2)))
      .force('collide', d3.forceCollide<N>(this.relationCircleR * 2.5).strength(0.6))
      .on('tick', this.ticked);
    (this.simulation.force('link') as any).links(this.links);
  }

  private ticked = () => {
    this.tick++;
    if (this.tick < tickDrawPoint) {
      // Do start of simulation without drawing
      return;
    }

    if (this.tick === tickDrawPoint) {
      this.createSimulationLinks();
      this.createSimulationNodes();
      this.setCenterNodeDetails();
    }

    this.redrawSimulationLinks();
    this.redrawSimulationNodes();
    this.redrawCenterNodeDetails();
  }

  private createSimulationLinks() {
    this.linkSvgElements = this.relationsCanvas
      .append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(this.links)
      .enter()
      .append('line')
      .attr('class', (link) => this.linkClass(link));

    this.linkLabelSvgElements = this.relationsCanvas
      .append('g')
      .attr('class', 'link-labels')
      .selectAll('text')
      .data(this.links)
      .enter()
      .append('text')
      .attr('class', (link) => this.linkLabelClass(link))
      .text((link) => this.linkLabelText(link));
  }

  private redrawSimulationLinks() {
    this.linkSvgElements
      .attr('x1', (link) => (link.source as N).x)
      .attr('y1', (link) => (link.source as N).y)
      .attr('x2', (link) => (link.target as N).x)
      .attr('y2', (link) => (link.target as N).y);

    this.linkLabelSvgElements.attr('x', (link) => this.linkLabelX(link)).attr('y', (link) => this.linkLabelY(link));
  }

  private createSimulationNodes() {
    this.nodeSvgElements = this.relationsCanvas
      .append('g')
      .attr('class', 'nodes')
      .selectAll('circle')
      .data(this.nodes)
      .enter()
      .append('circle')
      .attr('r', (node) => {
        if (node.element.Id === this.selectedElement.Id) {
          return this.centerCircleR;
        }
        return this.relationCircleR;
      })
      .attr('class', (node) => this.nodeClass(node))
      .on('click', (node) => this.handleNodeClick(node));

    this.nodeLabelSvgElements = this.relationsCanvas
      .append('g')
      .attr('class', 'name-labels')
      .selectAll('text')
      .data(this.nodes)
      .enter()
      .append('g')
      .attr('class', (node) => (node.element.Id === this.selectedElement.Id ? detailsInfoSelector : 'user-relation-name'))
      .append('text')
      .text((node) => (node.element.Id === this.selectedElement.Id ? '' : node.element.Name))
      .on('click', (node) => this.handleNodeClick(node));
  }

  private redrawSimulationNodes() {
    this.nodeSvgElements.attr('cx', (node) => this.nodeCX(node)).attr('cy', (node) => node.y);

    this.nodeLabelSvgElements
      .attr('x', (node, index, labels) => this.nodeLabelX(node, index, labels))
      .attr('y', (node) => (node.element.Id === this.selectedElement.Id ? node.y - 20 : node.y + 4));
  }

  protected setCenterNodeDetails() {
    const detailsInfoGroup = this.relationsCanvas.select(`.${detailsInfoSelector}`);
    const details = this.getCenterNodeDetails();

    this.centerTopDetails = detailsInfoGroup
      .append('text')
      .attr('class', 'context-details-info__name')
      .text(() => details.topText)
      .style('font-family', 'Lato, "Helvetica Neue", Helvetica, Arial, sans-serif');

    this.centerBottomDetails = detailsInfoGroup
      .append('text')
      .attr('class', 'context-details-info__extra')
      .text(() => details.bottomText)
      .style('font-family', 'Lato, "Helvetica Neue", Helvetica, Arial, sans-serif');
  }

  protected redrawCenterNodeDetails() {
    const centerCircleX = parseInt(this.nodeSvgElements.attr('cx'));
    const centerCircleY = parseInt(this.nodeSvgElements.attr('cy'));
    const topDetailsRect = this.centerTopDetails.node().getBoundingClientRect();
    this.centerTopDetails.attr(
      'transform',
      `translate(${centerCircleX - topDetailsRect.width / 2},${centerCircleY - this.centerCircleR - topDetailsRect.height + 10})`
    );

    const bottomDetailsRect = this.centerBottomDetails.node().getBoundingClientRect();
    this.centerBottomDetails.attr(
      'transform',
      `translate(${centerCircleX - bottomDetailsRect.width / 2}, ${centerCircleY + this.centerCircleR + 15})`
    );
  }
}
